// frontend/src/pages/Reinsurance/components/ContractManagement/ContractEdit/index.js

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faSave, faTimes, faExclamationCircle, faSync
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { reinsuranceService } from '../../../../../services/reinsurance';
import styles from './ContractEdit.module.css';

const ContractEdit = ({ contractId, onBack, onSuccess }) => {
  const { t } = useLanguage();
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  
  // Fetch contract details
  const fetchContractDetails = useCallback(async () => {
    if (!contractId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await reinsuranceService.getContract(contractId);
      
      if (response && response.success) {
        setContract(response.data);
        // Initialize form data with contract data
        setFormData({
          contract_type: response.data.contract_type || '',
          contract_structure: response.data.contract_structure || '',
          cedent: {
            cedent_name: response.data.cedent?.cedent_name || '',
            cedent_domicile: response.data.cedent?.cedent_domicile || ''
          },
          terms: {
            effective_date: response.data.terms?.effective_date || '',
            expiration_date: response.data.terms?.expiration_date || '',
            limit: response.data.terms?.limit || '',
            attachment_point: response.data.terms?.attachment_point || '',
            premium: {
              base_amount: response.data.terms?.premium?.base_amount || '',
              adjustable: response.data.terms?.premium?.adjustable || false
            }
          }
        });
      } else {
        throw new Error(response?.message || 'Failed to load contract details');
      }
    } catch (err) {
      console.error('Error fetching contract details:', err);
      setError(err.message || 'Failed to load contract details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [contractId]);
  
  // Fetch data on component mount
  useEffect(() => {
    fetchContractDetails();
  }, [fetchContractDetails]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name.includes('[') && name.includes(']')) {
      // Handle array properties
      const matches = name.match(/([^\[]+)\[([^\]]+)\]\.(.+)/);
      if (matches && matches.length === 4) {
        const [, parent, index, child] = matches;
        setFormData(prev => {
          const parentArray = [...(prev[parent] || [])];
          parentArray[index] = {
            ...parentArray[index],
            [child]: type === 'checkbox' ? checked : value
          };
          return {
            ...prev,
            [parent]: parentArray
          };
        });
      }
    } else {
      // Handle simple properties
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await reinsuranceService.updateContract(contractId, formData);
      
      if (response && response.success) {
        onSuccess(contractId);
      } else {
        throw new Error(response?.message || 'Failed to update contract');
      }
    } catch (err) {
      console.error('Error updating contract:', err);
      setError(err.message || 'Failed to update contract. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_contract_details')}</p>
      </div>
    );
  }
  
  if (error && !contract) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={fetchContractDetails}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.contractEdit}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_contract')}</span>
        </button>
        <h1 className={styles.title}>
          {t('edit_contract')}: {contract?.contract_number}
        </h1>
      </div>
      
      {error && (
        <div className={styles.error}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{error}</span>
        </div>
      )}
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'basic' ? styles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          {t('basic_information')}
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'terms' ? styles.active : ''}`}
          onClick={() => setActiveTab('terms')}
        >
          {t('terms')}
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'risk' ? styles.active : ''}`}
          onClick={() => setActiveTab('risk')}
        >
          {t('risk')}
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formContent}>
          {activeTab === 'basic' && (
            <div className={styles.basicTab}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('contract_details')}</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="contract_type">{t('contract_type')}</label>
                    <select
                      id="contract_type"
                      name="contract_type"
                      value={formData.contract_type || ''}
                      onChange={handleChange}
                      className={styles.select}
                      required
                    >
                      <option value="">{t('select_contract_type')}</option>
                      <option value="property">{t('property')}</option>
                      <option value="specialty">{t('specialty')}</option>
                      <option value="parametric">{t('parametric')}</option>
                      <option value="facultative">{t('facultative')}</option>
                      <option value="ilw">{t('ilw')}</option>
                    </select>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="contract_structure">{t('contract_structure')}</label>
                    <select
                      id="contract_structure"
                      name="contract_structure"
                      value={formData.contract_structure || ''}
                      onChange={handleChange}
                      className={styles.select}
                      required
                    >
                      <option value="">{t('select_contract_structure')}</option>
                      <option value="proportional">{t('proportional')}</option>
                      <option value="non_proportional">{t('non_proportional')}</option>
                      <option value="hybrid">{t('reinsurance_hybrid')}</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('cedent_information')}</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="cedent.cedent_name">{t('cedent_name')}</label>
                    <input
                      type="text"
                      id="cedent.cedent_name"
                      name="cedent.cedent_name"
                      value={formData.cedent?.cedent_name || ''}
                      onChange={handleChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="cedent.cedent_domicile">{t('cedent_domicile')}</label>
                    <input
                      type="text"
                      id="cedent.cedent_domicile"
                      name="cedent.cedent_domicile"
                      value={formData.cedent?.cedent_domicile || ''}
                      onChange={handleChange}
                      className={styles.input}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'terms' && (
            <div className={styles.termsTab}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('contract_terms')}</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="terms.effective_date">{t('effective_date')}</label>
                    <input
                      type="date"
                      id="terms.effective_date"
                      name="terms.effective_date"
                      value={formData.terms?.effective_date ? formData.terms.effective_date.split('T')[0] : ''}
                      onChange={handleChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="terms.expiration_date">{t('expiration_date')}</label>
                    <input
                      type="date"
                      id="terms.expiration_date"
                      name="terms.expiration_date"
                      value={formData.terms?.expiration_date ? formData.terms.expiration_date.split('T')[0] : ''}
                      onChange={handleChange}
                      className={styles.input}
                      required
                    />
                  </div>
                </div>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="terms.limit">{t('limit')}</label>
                    <input
                      type="number"
                      id="terms.limit"
                      name="terms.limit"
                      value={formData.terms?.limit || ''}
                      onChange={handleChange}
                      className={styles.input}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="terms.attachment_point">{t('attachment_point')}</label>
                    <input
                      type="number"
                      id="terms.attachment_point"
                      name="terms.attachment_point"
                      value={formData.terms?.attachment_point || ''}
                      onChange={handleChange}
                      className={styles.input}
                      min="0"
                    />
                  </div>
                </div>
              </div>
              
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('premium_details')}</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="terms.premium.base_amount">{t('base_premium')}</label>
                    <input
                      type="number"
                      id="terms.premium.base_amount"
                      name="terms.premium.base_amount"
                      value={formData.terms?.premium?.base_amount || ''}
                      onChange={handleChange}
                      className={styles.input}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <div className={styles.checkboxGroup}>
                      <input
                        type="checkbox"
                        id="terms.premium.adjustable"
                        name="terms.premium.adjustable"
                        checked={formData.terms?.premium?.adjustable || false}
                        onChange={handleChange}
                        className={styles.checkbox}
                      />
                      <label htmlFor="terms.premium.adjustable">{t('adjustable_premium')}</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'risk' && (
            <div className={styles.riskTab}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('risk_information')}</h3>
                <p className={styles.comingSoon}>{t('risk_edit_coming_soon')}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className={styles.formActions}>
          <button 
            type="button" 
            className={styles.cancelButton} 
            onClick={onBack}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span>{t('cancel')}</span>
          </button>
          <button 
            type="submit" 
            className={styles.saveButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className={styles.spinner}></div>
                <span>{t('saving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span>{t('save_changes')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractEdit;